import React, { ReactNode } from "react";
import classnames from "classnames";

import Typography from "@vahak/core-ui/dist/components/Typography";
import Flex from "@vahak/core-ui/dist/layout/Flex";
import Button from "@vahak/core-ui/dist/components/Button";
import COLORS from "@vahak/core-ui/dist/constants/colors";
import { GA4EventNames } from "@vahak/core/dist/constants/event-names";

import useValidateUserStatus from "../../../hooks/useValidateUserStatus";
import { POSTING_FORM_MODAL_ORIGINATION_PAGE, useLoadLorryPosting } from "../../../context/load-lorry-posting";
import useOpenNewLoadPosting from "../../../hooks/useOpenNewLoadPosting";
import { LORRY_CREATION_FORM_IDS } from "../../posting/helpers/constants";
import AppImg from "../../utils/AppImg";
import { useEventTrackerService } from "@vahak/core/dist/_services/hooks/useEventTrackerService";
import { MARKETPLACE_POSTING_NUDGE_CONTENT, MarketplacePostingNudgeType } from "./marketplacePostingNudge.constants";
import Styles from "./MarketplacePostingNudgeCard.module.scss";

interface MarketplacePostingNudgeCardProps {
    type: MarketplacePostingNudgeType;
}

const MarketplacePostingNudgeCard = ({ type }: MarketplacePostingNudgeCardProps) => {
    const { ValidateLorryPosting } = useValidateUserStatus();
    const loadLorryPostingConfig = useLoadLorryPosting();
    const openNewLoadPosting = useOpenNewLoadPosting();
    const { sendGAandMoEngageEvent } = useEventTrackerService();
    const content = MARKETPLACE_POSTING_NUDGE_CONTENT[type];

    const handleClick = () => {
        if (type === "load") {
            sendGAandMoEngageEvent({
                name: GA4EventNames.MARKET_PLACE.post_load_button_click,
                data: { source_screen: "marketplace" }
            });
            openNewLoadPosting({
                originationPage: POSTING_FORM_MODAL_ORIGINATION_PAGE.MARKETPLACE
            });
            return;
        }

        ValidateLorryPosting(() => {
            setTimeout(() => {
                sendGAandMoEngageEvent({
                    name: GA4EventNames.MARKET_PLACE.add_lorry_attach_lorry_btn,
                    data: { source_screen: "marketplace" }
                });
                loadLorryPostingConfig?.updateLoadLorryPostingData({
                    isModalOpen: true,
                    category: "post",
                    originationPage: POSTING_FORM_MODAL_ORIGINATION_PAGE.MARKETPLACE,
                    type: "lorry"
                });
                loadLorryPostingConfig?.openLorryPostingModal(LORRY_CREATION_FORM_IDS.VEHICLE_NUMBER);
            }, 1000);
        });
    };

    return (
        <Flex className={classnames(Styles.main, Styles[type])} gap={20} flexDirection="column">
            <AppImg {...content.image} unoptimized />
            <Flex flexDirection="column" gap={20}>
                <div className={Styles.textWrapper}>
                    <Typography color={COLORS.WHITE} weight="medium" align="center" mSize="sm">
                        {content.label}
                    </Typography>
                </div>
                <Button isRounded type="button" color="tertiary" blockBtn onClick={handleClick}>
                    {content.cta}
                </Button>
            </Flex>
        </Flex>
    );
};

interface MarketplacePostingNudgeCardsProps {
    types: readonly MarketplacePostingNudgeType[];
}

export const MarketplacePostingNudgeCards = ({ types }: MarketplacePostingNudgeCardsProps) => (
    <>
        {types.map((type) => (
            <MarketplacePostingNudgeCard key={type} type={type} />
        ))}
    </>
);

interface MarketplaceListItemWithNudgesProps {
    nudgeTypes: readonly MarketplacePostingNudgeType[];
    showBefore: boolean;
    showAfter: boolean;
    children: ReactNode;
}

export const MarketplaceListItemWithNudges = ({
    nudgeTypes,
    showBefore,
    showAfter,
    children
}: MarketplaceListItemWithNudgesProps) => (
    <>
        {showBefore ? <MarketplacePostingNudgeCards types={nudgeTypes} /> : null}
        {children}
        {showAfter ? <MarketplacePostingNudgeCards types={nudgeTypes} /> : null}
    </>
);

export default MarketplacePostingNudgeCard;
